// Anzeige-Helfer fuer Rezepte: Kategorien, Zeiten, Mengen, Tags, Textbereinigung.

export type KategorieSlug = 'suess_fruehstueck' | 'brot_snack' | 'salat_bowl' | 'suppe_curry' | 'ofen_pfanne';

export const KATEGORIEN: ReadonlyArray<{ slug: KategorieSlug; label: string }> = [
  { slug: 'suess_fruehstueck', label: 'Süßes & Frühstück' },
  { slug: 'brot_snack', label: 'Brot, Wraps & Snacks' },
  { slug: 'salat_bowl', label: 'Salate & Bowls' },
  { slug: 'suppe_curry', label: 'Suppen, Currys & Eintöpfe' },
  { slug: 'ofen_pfanne', label: 'Ofen, Pfanne & Pasta' },
];

/** CSS-Variable der Kategorie-Hintergrundfarbe (GL-023 §12.2), ohne Kategorie die ruhige Flaeche. */
export function kategorieFarbe(k: string | null | undefined): string {
  return k ? `var(--nb-category-bg-${k.replace('_', '-')})` : 'var(--nb-color-surface-subtle)';
}

/** 30 -> "30 Min", 290 -> "4 Std 50 Min", 1440 -> "24 Std". Ohne Zeit: leer, die Stelle laesst das Feld weg (QA W9 Befund 2). */
export function zeitText(min: number | null | undefined): string {
  if (min === null || min === undefined || min <= 0) return '';
  if (min < 60) return `${min} Min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} Std ${m} Min` : `${h} Std`;
}

export function kcalText(kcal: number | null | undefined): string {
  return kcal === null || kcal === undefined ? '' : `${Math.round(kcal)} kcal`;
}

const TAGS: Record<string, { label: string; hervor?: boolean }> = {
  lipoedem_freundlich: { label: 'Lipödem-freundlich', hervor: true },
  lipoedem_teilweise: { label: 'Lipödem: teils passend' },
  lipoedem_ausnahme: { label: 'Ausnahme-Rezept' },
  anti_inflammatorisch: { label: 'Antientzündlich' },
  vegetarisch: { label: 'Vegetarisch' },
  high_protein: { label: 'Eiweißreich' },
  schnell: { label: 'Schnell' },
  meal_prep_hero: { label: 'Gut vorzukochen' },
  fisch: { label: 'Fisch' },
  low_carb: { label: 'Low Carb' },
  airfryer: { label: 'Heißluftfritteuse' },
};

/** Merkmale, die man im Formular an- und abwaehlen kann (in Anzeige-Reihenfolge). */
export const MERKMALE: ReadonlyArray<{ tag: string; label: string }> = Object.entries(TAGS).map(([tag, d]) => ({ tag, label: d.label }));

/** Tags mit deutschem Label. Interne Merkmale (deconstructable, thermomix) bleiben unsichtbar. */
export function tagsFuerAnzeige(tags: readonly string[]): Array<{ tag: string; label: string; hervor: boolean }> {
  return tags
    .flatMap((t) => {
      const d = TAGS[t];
      return d ? [{ tag: t, label: d.label, hervor: Boolean(d.hervor) }] : [];
    })
    .sort((a, b) => Number(b.hervor) - Number(a.hervor));
}

/**
 * Rezepttexte sind wortgleich uebernommen (Nora, W1c) und enthalten Gedankenstriche.
 * In der Oberflaeche gibt es keine: Bereiche werden "bis", eingeschobene Striche ein Komma.
 */
export function ohneGedankenstriche(t: string): string {
  return t
    .replace(/(\d)\s*[–—]\s*(\d)/g, '$1 bis $2')
    .replace(/\s+[–—]\s+/g, ', ')
    .replace(/[–—]/g, '-');
}

/** Zerlegt "**fett** normal" in Teile, damit die Seite <strong> setzen kann. */
export function fettTeile(t: string): Array<{ text: string; fett: boolean }> {
  const teile: Array<{ text: string; fett: boolean }> = [];
  ohneGedankenstriche(t)
    .split('**')
    .forEach((stueck, i) => {
      if (stueck) teile.push({ text: stueck, fett: i % 2 === 1 });
    });
  return teile;
}

const BRUECHE: ReadonlyArray<[number, string]> = [
  [0.25, '¼'],
  [0.333, '⅓'],
  [0.5, '½'],
  [0.667, '⅔'],
  [0.75, '¾'],
];

function zahlText(x: number, metrisch: boolean): string {
  if (metrisch) {
    // Lyra SPEZ 4.2 (W8, gilt fuer Rezeptseite und Kochmodus): ab 100 auf 10, ab 10 auf 5, darunter eine Stelle
    // (2 g Hefe bleiben 2 g). Nie 0.
    const gerundet = x >= 100 ? Math.round(x / 10) * 10 : x >= 10 ? Math.max(5, Math.round(x / 5) * 5) : Math.max(0.5, Math.round(x * 10) / 10);
    return gerundet.toLocaleString('de-DE');
  }
  const ganz = Math.floor(x + 1e-6);
  const rest = x - ganz;
  if (rest < 0.08) return String(ganz || (x > 0 ? '¼' : '0'));
  if (rest > 0.92) return String(ganz + 1);
  const bruch = BRUECHE.find(([w]) => Math.abs(rest - w) < 0.06);
  if (bruch) return ganz ? `${ganz}${bruch[1]}` : bruch[1];
  return (Math.round(x * 10) / 10).toLocaleString('de-DE');
}

const PLURAL: ReadonlyArray<[RegExp, string]> = [
  [/^Dose\b/, 'Dosen'],
  [/^Packung\b/, 'Packungen'],
  [/^Prise\b/, 'Prisen'],
  [/^Stange\b/, 'Stangen'],
  [/^Kugel\b/, 'Kugeln'],
  [/^Scheibe\b/, 'Scheiben'],
  [/^Filet\b/, 'Filets'],
  [/^Kopf\b/, 'Köpfe'],
  [/^Zehe\b/, 'Zehen'],
  [/^Zweig\b/, 'Zweige'],
];

const METRISCH = new Set(['g', 'kg', 'ml', 'l', 'cm']);

/**
 * Menge fuer die Zutatenzeile, auf die gewaehlten Portionen umgerechnet.
 * faktor = gewaehlte Portionen / Rezeptportionen.
 */
export function mengeText(
  z: { menge: number | null; einheit: string | null; nach_geschmack: boolean },
  faktor: number,
): string {
  if (z.menge === null) {
    if (z.nach_geschmack) return 'nach Geschmack';
    return z.einheit ? z.einheit : 'nach Bedarf';
  }
  const wert = z.menge * faktor;
  // Ab 1000 g bzw. ml als kg bzw. l mit einer Stelle ("1,2 l", Lyra SPEZ 4.2)
  if ((z.einheit === 'g' || z.einheit === 'ml') && wert >= 1000) {
    const gross = Math.round(wert / 100) / 10;
    return `${gross.toLocaleString('de-DE')} ${z.einheit === 'g' ? 'kg' : 'l'}`;
  }
  const metrisch = z.einheit !== null && METRISCH.has(z.einheit);
  const zahl = zahlText(wert, metrisch);
  if (!z.einheit) return zahl;
  let einheit = z.einheit;
  if (wert > 1.01) {
    const p = PLURAL.find(([muster]) => muster.test(einheit));
    if (p) einheit = einheit.replace(p[0], p[1]);
  }
  return `${zahl} ${einheit}`;
}

export const NAEHRSTOFF_LABEL: Record<string, string> = {
  kcal: 'Energie',
  eiweiss_g: 'Eiweiß',
  kohlenhydrate_g: 'Kohlenhydrate',
  fett_g: 'Fett',
  ballaststoffe_g: 'Ballaststoffe',
};

export function naehrwertText(naehrstoff: string, wert: number): string {
  if (naehrstoff === 'kcal') return `${Math.round(wert)} kcal / ${Math.round(wert * 4.184)} kJ`;
  return `${(Math.round(wert * 10) / 10).toLocaleString('de-DE')} g`;
}

export const STATUS_WORT: Record<string, string | undefined> = { berechnet: 'berechnet', geschaetzt: 'geschätzt' };

/**
 * Kaufbare Menge (Lyra W9 A1, Rechenweg in ENTSCHEIDUNGEN.md): auf der Einkaufsliste stehen keine Drittel.
 * Gezaehltes (Stück, Zehe, Bund, Dose, Packung, ...) wird auf ganze Einheiten aufgerundet, EL/TL auf ½,
 * g/ml unter 10 auf 1, unter 100 auf 5, ab 100 auf 10, ab 1000 als kg/l mit einer Stelle (immer aufrunden).
 * Gerundet wird erst die Summe aller Rezepte, nie die einzelnen Zeilen.
 */
export function kaufbar(menge: number, einheit: string | null): { zahl: string; einheit: string | null; wert: number } {
  const e = (einheit ?? '').trim();
  const auf = (x: number, schritt: number) => Math.ceil(x / schritt - 1e-9) * schritt;
  if (e === 'g' || e === 'ml') {
    if (menge >= 1000) {
      const w = auf(menge, 100) / 1000;
      return { zahl: w.toLocaleString('de-DE'), einheit: e === 'g' ? 'kg' : 'l', wert: w };
    }
    const w = Math.max(1, menge >= 100 ? auf(menge, 10) : menge >= 10 ? auf(menge, 5) : auf(menge, 1));
    return { zahl: String(w), einheit: e, wert: w };
  }
  if (e === 'kg' || e === 'l') {
    const w = Math.round(auf(menge, 0.1) * 10) / 10;
    return { zahl: w.toLocaleString('de-DE'), einheit: e, wert: w };
  }
  if (e === 'cm') return { zahl: String(auf(menge, 1)), einheit: e, wert: auf(menge, 1) };
  if (/^(geh\.\s*)?(EL|TL)$/i.test(e)) {
    const w = Math.max(0.5, auf(menge, 0.5));
    return { zahl: zahlText(w, false), einheit: e, wert: w };
  }
  // Alles andere wird gezaehlt: ganze Einheiten, mindestens 1
  const w = Math.max(1, auf(menge, 1));
  return { zahl: String(w), einheit: e || 'Stück', wert: w };
}

function mehrzahl(einheit: string, wert: number): string {
  if (wert <= 1.01) return einheit;
  const p = PLURAL.find(([muster]) => muster.test(einheit));
  return p ? einheit.replace(p[0], p[1]) : einheit;
}

/**
 * Menge einer Einkaufszeile oder Gruppe (Canvas R4 Einkaufen): "2 × 400 g" fuer Gebinde mit Inhaltsangabe,
 * "3 × daumengroß" fuer Zeilen ohne Zahl, mehrere Einheiten mit " + ". kaufbar = aufgerundet (Liste),
 * sonst der genaue Anteil wie im Rezept (aufgeklappte Rezeptzeilen einer Sammelzeile).
 */
export function einkaufMengeText(
  teile: ReadonlyArray<{ menge: number | null; einheit: string | null; anzahl: number }>,
  { genau = false }: { genau?: boolean } = {},
): string {
  return teile
    .map((t) => {
      // Gebinde mit Inhaltsangabe: "Dose (400 g)", "Filet (125 g)", "Packung (200 g)" -> "2 × 400 g"
      const gebinde = t.einheit?.match(/^[A-Za-zÄÖÜäöüß]+\s*\((.+)\)$/);
      if (t.menge === null) {
        if (!t.einheit) return '';
        return t.anzahl > 1 ? `${t.anzahl} × ${t.einheit}` : t.einheit;
      }
      if (genau) {
        if (gebinde?.[1]) return `${zahlText(t.menge, false)} × ${gebinde[1]}`;
        return mengeText({ menge: t.menge, einheit: t.einheit, nach_geschmack: false }, 1);
      }
      if (gebinde?.[1]) return `${Math.max(1, Math.ceil(t.menge - 1e-9))} × ${gebinde[1]}`;
      const k = kaufbar(t.menge, t.einheit);
      return k.einheit ? `${k.zahl} ${mehrzahl(k.einheit, k.wert)}` : k.zahl;
    })
    .filter(Boolean)
    .join(' + ');
}

export interface WarumGut {
  einleitung: string;
  punkte: Array<{ zutat: string | null; text: string }>;
}

/**
 * "Warum das gut tut" als Text (W7a): erste Zeilen ohne Spiegelstrich = Einleitung, Zeilen mit "- " oder "• "
 * = Stichpunkt, der Teil vor dem ersten Doppelpunkt ist die Zutat (fett, Canvas R3-T1-Start). Alte Fliesstexte
 * ohne Stichpunkte bleiben eine Einleitung. So bleibt das Feld im Formular frei bearbeitbar.
 */
export function warumZerlegen(t: string): WarumGut {
  const einleitung: string[] = [];
  const punkte: WarumGut['punkte'] = [];
  for (const roh of ohneGedankenstriche(t).split('\n')) {
    const zeile = roh.trim();
    if (!zeile) continue;
    // Punkt = Zeile mit Spiegelstrich, oder (Lyra W9 B2, Hilfetext ohne Markdown) nach der Einleitung eine Zeile
    // "Zutat: Text" mit kurzer Zutat vorn
    const m = /^[-•*]\s+(.*)$/.exec(zeile) ?? (einleitung.length > 0 && /^[^:]{1,40}:\s+\S/.test(zeile) ? [zeile, zeile] : null);
    const letzter = punkte.at(-1);
    if (!m) {
      if (letzter) letzter.text += ` ${zeile}`;
      else einleitung.push(zeile);
      continue;
    }
    const inhalt = (m[1] ?? '').replace(/\*\*/g, '');
    const z = /^([^:]{1,60}):\s+(.+)$/.exec(inhalt);
    punkte.push(z ? { zutat: (z[1] ?? '').trim(), text: (z[2] ?? '').trim() } : { zutat: null, text: inhalt });
  }
  return { einleitung: einleitung.join(' ').replace(/\*\*/g, ''), punkte };
}

/** "Hähnchen" = "haehnchen" = "hahnchen" (QA W9 Befund 14): Umlaute und ß in beiden Schreibweisen. */
export function grundform(t: string): string {
  return t
    .toLocaleLowerCase('de')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ß/g, 'ss')
    .replace(/([aou])e/g, '$1');
}

/** Suche ueber einen Suchtext (Name, Zutaten, Tags): alle Woerter muessen vorkommen. Entdecken und Gericht-Wahl. */
export function passtZurSuche(suchtext: string, begriff: string): boolean {
  const woerter = grundform(begriff).split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return true;
  const t = grundform(suchtext);
  return woerter.every((w) => t.includes(w));
}
